import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Checkbox, FormControlLabel, FormGroup } from '@mui/material';

//core components
import { AppFormInput, AppFormSelect, AppSubmitButton, AppForm, AppFormContext } from 'components/new_components/forms';
import AppCenteredModal from 'components/new_components/AppCenteredModal';

// others
import { userValidate } from './utils/validation';
import { getAvailableUserRoles } from './utils/constants';

// redux
import { connect, useSelector } from 'react-redux';
import { AddMerchantUser, UpdateMerchantSupportUser, UpdateMerchantUser } from 'store/actions/merchantActions';
import { GetAllSupportUsers } from 'store/actions/adminActions';
import { useTranslation } from 'react-i18next';
import { all_standards } from '../Compliance/utils';
import CustomTooltip from 'components/new_components/CustomTooltip';
import { HelpCircle } from 'lucide-react';
import { CloseOutlined } from '@ant-design/icons';

const UserModal = (props) => {
    const {
        open,
        handleClose,
        payload,
        isAddModal,
        AddMerchantUser,
        GetAllSupportUsers,
        all_support_users,
        UpdateMerchantSupportUser,
        merchant_standards,
        UpdateMerchantUser,
        hasSupport,
    } = props;
    const [loading, setLoading] = useState(false);
    const [values, setValues] = useState({});
    // const [routeList, setRouteList] = useState([]);
    const [supportUsers, setSupportUsers] = useState([]);
    const [standards, setStandards] = useState([]);
    const [frameworks, setFrameworks] = useState([]);

    //translation
    const { t } = useTranslation('user');

    const user_info = useSelector((state) => state?.generalReducers?.user_info);

    // functions
    // logic functions
    const getCurrentValues = (values) => setValues(values);
    const handleSubmit = (values) => {
        const payload = getPayload(values);
        if (isAddModal) {
            handleUserAdd({ ...payload, active_standards: frameworks?.join(',') }, values.role);
        } else {
            handleUserEdit({ ...payload, active_standards: frameworks?.join(',') }, values.role, values.id);
        }
    };
    const getPayload = (user) => {
        const { email, role, auditor_standards, support_user, routes, first_name } = user;
        let payload = {};
        if (user.role !== 'support') {
            payload.email = email;
            payload.role = role;
            payload.first_name = first_name;
            if (user.role === 'auditor') {
                payload.auditor_standards = auditor_standards?.join(',');
            }
        } else {
            payload.support_user = support_user;
            payload.support_routes = routes.join(',');
        }
        return payload;
    };

    // async functions
    const handleUserAdd = async (user, role) => {
        setLoading(true);
        const res = role === 'support' ? await UpdateMerchantSupportUser(user) : await AddMerchantUser(user);
        setLoading(false);
        if (res?.success) {
            if (res?.message !== 'Upsell' && res?.message !== 'Paywall') {
                toast.success('User added successfully.');
            }
            handleClose();
        } else {
            if (res?.message !== 'Upsell' && res?.message !== 'Paywall') {
                toast.error(res?.message);
            }
        }
    };
    const handleUserEdit = async (user, role, id) => {
        setLoading(true);
        const res = role === 'support' ? await UpdateMerchantSupportUser(user) : await UpdateMerchantUser(user, id);
        setLoading(false);
        if (res?.success) {
            toast.success(res?.message);
            handleClose();
        } else {
            toast.error(res?.message);
        }
    };

    // useEffect
    // useEffect(() => {
    //     const routeList = [].merchant.flatMap((route) => {
    //         if (route.subMenus) {
    //             return route.subMenus?.map((submenu) => ({
    //                 value: submenu.path,
    //                 name: `${route.name} - ${submenu.name}`,
    //             }));
    //         } else {
    //             return { name: route.name, value: route.path };
    //         }
    //     });
    //     setRouteList(routeList);
    // }, []);
    useEffect(() => {
        if (open) {
            GetAllSupportUsers();
            setFrameworks([]);
        }
    }, [open]);
    useEffect(() => {
        const supportUsers = all_support_users?.map((user) => {
            return {
                value: user.id,
                name: `${user?.user_detail?.first_name} ${user?.user_detail?.last_name}`,
            };
        });
        setSupportUsers(supportUsers);
    }, [all_support_users]);
    useEffect(() => {
        let uniqueStandards = [...new Set(merchant_standards?.toLowerCase()?.split(','))];

        setStandards(
            uniqueStandards?.map((standard) => ({
                value: standard,
                name: standard?.toUpperCase(),
            }))
        );
    }, [merchant_standards]);

    useEffect(() => {
        payload.active_standards && setFrameworks(payload.active_standards);
    }, [payload]);

    return (
        <AppForm initialValues={payload} onSubmit={handleSubmit} validate={userValidate}>
            <AppCenteredModal
                noBorder
                open={open}
                handleClose={handleClose}
                title={<span className="text-[#202d66]">{isAddModal ? t('createUser') : t('updateUser')}</span>}
                width={470}
                dialogActionsStyle={{
                    bgcolor: '#F8FAFC',
                }}
                headerAction={
                    <div
                        onClick={handleClose}
                        className="cursor-pointer border border-[#E2E8F0] rounded-full px-1 flex items-center justify-center py-0 w-[22px] h-[22px]"
                    >
                        <CloseOutlined className="text-[9px] text-[#334155]" />
                    </div>
                }
                actions={
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'end',
                            py: 1.5,
                            gap: 1,
                            bgcolor: '##F8FAFC',
                        }}
                    >
                        <button
                            size="small"
                            onClick={handleClose}
                            className="!text-capitalize font-medium border border-[#E2E8F0] text-[#334155] bg-white rounded-sm text-sm px-[15px] py-[5px]"
                        >
                            Cancel
                        </button>
                        <AppSubmitButton
                            text={isAddModal ? t('createNewUser') : t('updateUser')}
                            variant="contained"
                            color="primary"
                            loading={loading}
                            loadingPosition="center"
                            sx={{
                                width: 'fit-content',
                                background: '#202D66',
                                px: '15px !important',
                                py: '5px',
                                fontWeight: '500 !important',
                                boxShadow: 'none',
                                borderRadius: '0.2rem',
                            }}
                        />
                    </Box>
                }
            >
                <AppFormContext getValues={getCurrentValues}>
                    {values.role === 'support' && !isAddModal ? (
                        <AppFormInput
                            type="text"
                            name="role"
                            label={t('userModal.roleInput')}
                            value={'Support'}
                            disabled={!isAddModal && payload?.role === 'support'} //disable if it's an edit modal for support so they can't change their roles
                        />
                    ) : (
                        <>
                            <AppFormInput
                                type="text"
                                name="first_name"
                                label={t('userModal.inputFirstname.label')}
                                disabled={!isAddModal && payload?.first_name} //disable if it's an edit modal so they can't change employee names
                                placeholder={t('userModal.inputFirstname.placeholder')}
                            />
                            <AppFormSelect
                                name="role"
                                label={t('userModal.roleInput')}
                                options={getAvailableUserRoles(hasSupport, isAddModal)}
                                defaultValue={t('userModal.inputSelect1.defaultValue')}
                                menuItemSx={{
                                    '&.Mui-selected': {
                                        backgroundColor: '#f1f5f9',
                                        color: '#64748b',
                                        '&:hover': {
                                            backgroundColor: '#f1f5f9',
                                            color: '#64748b',
                                        },
                                    },
                                }}
                                defaultValueSx={{
                                    color: '#64748b',
                                    '&:hover': {
                                        backgroundColor: '#f1f5f9',
                                        color: '#64748b',
                                    },
                                }}
                                sx={{
                                    fontSize: '13px',
                                }}
                                MenuProps={{
                                    MenuListProps: {
                                        boxShadow: 'none !important',
                                    },
                                }}
                            />
                        </>
                    )}
                    {values.role === 'auditor' && (
                        <AppFormSelect
                            name="auditor_standards"
                            label={t('userModal.inputSelect2.label')}
                            options={standards}
                            defaultValue={t('userModal.inputSelect2.defaultValue')}
                            multiple
                        />
                    )}
                    {values.role === 'support' ? (
                        <>
                            <AppFormSelect
                                name="support_user"
                                label={t('userModal.inputSelect3.label')}
                                options={supportUsers}
                                defaultValue={t('userModal.inputSelect3.defaultValue')}
                            />
                            <AppFormSelect
                                name="routes"
                                label={t('userModal.inputSelect4.label')}
                                options={[]}
                                defaultValue={t('userModal.inputSelect4.defaultValue')}
                                multiple
                            />
                        </>
                    ) : (
                        <>
                            <AppFormInput
                                type="email"
                                label={t('userModal.inputLabel1')}
                                name="email"
                                placeholder={t('userModal.inputPlaceholder1')}
                                disabled={!isAddModal}
                            />

                            {/* allow only merchants edit a user's framework privilege */}
                            {user_info?.user_type === 'merchant' ? (
                                <div className="mt-4 pb-4">
                                    <div className="flex gap-1 items-center mb-3">
                                        <label className="mb-0 text-[13px] font-semibold text-[#64748B]">
                                            Framework privilege
                                        </label>
                                        <CustomTooltip
                                            title="Specify the compliance framework to which this user will be granted access."
                                            placement="top"
                                        >
                                            <div className="flex flex-shrink-0">
                                                <HelpCircle color="#64748B" className="w-[15px] h-[15px]" />
                                            </div>
                                        </CustomTooltip>
                                    </div>{' '}
                                    <FormGroup
                                        sx={{
                                            flexDirection: 'row',
                                            flexWrap: 'wrap',
                                            gap: '9px',
                                        }}
                                    >
                                        {all_standards?.map((standard) => (
                                            <FormControlLabel
                                                sx={{
                                                    color: '#002C72',
                                                    textTransform: 'uppercase',
                                                    m: 0,
                                                    '& span': {
                                                        fontSize: '14px !important',
                                                        fontWeight: 500,
                                                    },
                                                }}
                                                control={
                                                    <Checkbox
                                                        name={standard}
                                                        checked={frameworks?.includes(standard)}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            if (checked) {
                                                                setFrameworks((prev) => [...prev, standard]);
                                                            } else {
                                                                setFrameworks((prev) =>
                                                                    prev.filter((item) => item !== standard)
                                                                );
                                                            }
                                                        }}
                                                        size="small"
                                                        sx={{
                                                            color: '#002C72',
                                                            py: 0,
                                                            pl: 0,
                                                        }}
                                                    />
                                                }
                                                label={standard}
                                            />
                                        ))}
                                    </FormGroup>
                                </div>
                            ) : null}
                        </>
                    )}
                </AppFormContext>
            </AppCenteredModal>
        </AppForm>
    );
};
const mapStateToProps = (state) => {
    return {
        all_support_users: state?.adminReducers?.all_support_users,
        merchant_standards: state?.generalReducers?.user_info?.organization?.standards,
        hasSupport: Boolean(state?.generalReducers?.user_info?.organization?.support_user),
    };
};
export default connect(mapStateToProps, {
    AddMerchantUser,
    GetAllSupportUsers,
    UpdateMerchantSupportUser,
    UpdateMerchantUser,
})(UserModal);
